/**
 * CustomerDetailView - Company/Client Dashboard
 *
 * Shows a comprehensive view of a customer/client including:
 * - Company information
 * - Associated contacts (people from that company)
 * - Related projects
 * - Invoices history
 * - Statistics and summaries
 */

import React, { useMemo } from 'react';
import {
  ArrowLeft,
  Building2,
  User,
  Mail,
  Phone,
  MapPin,
  FileText,
  FolderKanban,
  Users,
  TrendingUp,
  TrendingDown,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Edit,
  ExternalLink,
} from 'lucide-react';
import type { Customer, Invoice } from '@business-assistant/shared/data/types';
import type { Contact, Project } from '@business-assistant/shared/domain';
import { invoices as invoicesData } from '@business-assistant/shared/data/accounting';
import { formatEUR } from '@business-assistant/shared';

// =============================================================================
// TYPES
// =============================================================================

interface CustomerDetailViewProps {
  customer: Customer;
  contacts?: Contact[];
  projects?: Project[];
  onBack: () => void;
  onEdit: () => void;
  onContactClick?: (contactId: string) => void;
  onProjectClick?: (projectId: string) => void;
  onInvoiceClick?: (invoiceId: string) => void;
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

function getCustomerDisplayName(customer: Customer): string {
  if (customer.customerType === 'company') {
    return customer.companyName || 'N/A';
  }
  return `${customer.firstName || ''} ${customer.lastName || ''}`.trim() || 'N/A';
}

function formatDate(date: string): string {
  return new Date(date).toLocaleDateString('it-IT', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function formatCurrency(cents: number): string {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
  }).format(cents / 100);
}

// =============================================================================
// SUB-COMPONENTS
// =============================================================================

function StatCard({
  icon,
  label,
  value,
  subvalue,
  color,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  subvalue?: string;
  color: string;
}) {
  return (
    <div
      style={{
        background: 'white',
        borderRadius: '0.5rem',
        padding: '1rem',
        border: '1px solid var(--line, #e5e7eb)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
        <div
          style={{
            width: '32px',
            height: '32px',
            borderRadius: '0.375rem',
            background: color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
          }}
        >
          {icon}
        </div>
        <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary, #6b7280)' }}>{label}</span>
      </div>
      <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>{value}</div>
      {subvalue && (
        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted, #9ca3af)', marginTop: '0.25rem' }}>
          {subvalue}
        </div>
      )}
    </div>
  );
}

function SectionHeader({ icon, title, count }: { icon: React.ReactNode; title: string; count?: number }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        padding: '0.75rem 0',
        borderBottom: '1px solid var(--line, #e5e7eb)',
        marginBottom: '0.75rem',
      }}
    >
      {icon}
      <span style={{ fontWeight: 600, fontSize: '0.9375rem' }}>{title}</span>
      {count !== undefined && (
        <span
          style={{
            background: 'var(--bg, #f3f4f6)',
            padding: '0.125rem 0.5rem',
            borderRadius: '9999px',
            fontSize: '0.75rem',
            fontWeight: 500,
          }}
        >
          {count}
        </span>
      )}
    </div>
  );
}

function ContactCard({
  contact,
  onClick,
}: {
  contact: Contact;
  onClick?: () => void;
}) {
  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        padding: '0.75rem',
        background: 'white',
        borderRadius: '0.5rem',
        border: '1px solid var(--line, #e5e7eb)',
        cursor: onClick ? 'pointer' : 'default',
      }}
    >
      <div
        style={{
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          background: 'var(--brand, #3b82f6)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 600,
        }}
      >
        {contact.displayName[0]?.toUpperCase() || '?'}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 500 }}>{contact.displayName}</div>
        {contact.role && (
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted, #9ca3af)' }}>{contact.role}</div>
        )}
      </div>
      {contact.handles.email && (
        <Mail size={16} style={{ color: 'var(--text-muted, #9ca3af)' }} />
      )}
      {contact.handles.phone && (
        <Phone size={16} style={{ color: 'var(--text-muted, #9ca3af)' }} />
      )}
    </div>
  );
}

function ProjectCard({
  project,
  onClick,
}: {
  project: Project;
  onClick?: () => void;
}) {
  const statusColors: Record<string, { bg: string; color: string }> = {
    'da-iniziare': { bg: '#dbeafe', color: '#3b82f6' },
    'in-corso': { bg: '#dcfce7', color: '#22c55e' },
    'a-rischio': { bg: '#fef3c7', color: '#f59e0b' },
    'in-pausa': { bg: '#f3f4f6', color: '#6b7280' },
    'chiuso': { bg: '#f3f4f6', color: '#6b7280' },
  };
  const statusLabels: Record<string, string> = {
    'da-iniziare': 'Da iniziare',
    'in-corso': 'In corso',
    'a-rischio': 'A rischio',
    'in-pausa': 'In pausa',
    'chiuso': 'Chiuso',
  };
  const style = statusColors[project.status] || statusColors['in-corso'];

  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        padding: '0.75rem',
        background: 'white',
        borderRadius: '0.5rem',
        border: '1px solid var(--line, #e5e7eb)',
        cursor: onClick ? 'pointer' : 'default',
      }}
    >
      <FolderKanban size={20} style={{ color: style.color }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 500 }}>{project.title}</div>
        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted, #9ca3af)' }}>
          {project.dueAt ? `Scadenza: ${formatDate(project.dueAt)}` : 'Nessuna scadenza'}
        </div>
      </div>
      <span
        style={{
          padding: '0.25rem 0.5rem',
          borderRadius: '9999px',
          fontSize: '0.6875rem',
          fontWeight: 500,
          background: style.bg,
          color: style.color,
        }}
      >
        {statusLabels[project.status] || project.status}
      </span>
    </div>
  );
}

function InvoiceCard({
  invoice,
  onClick,
}: {
  invoice: Invoice;
  onClick?: () => void;
}) {
  const statusColors: Record<string, { bg: string; color: string }> = {
    draft: { bg: '#f3f4f6', color: '#6b7280' },
    sent: { bg: '#dbeafe', color: '#3b82f6' },
    paid: { bg: '#dcfce7', color: '#22c55e' },
    overdue: { bg: '#fee2e2', color: '#ef4444' },
    cancelled: { bg: '#f3f4f6', color: '#9ca3af' },
  };
  const statusLabels: Record<string, string> = {
    draft: 'Bozza',
    sent: 'Inviata',
    paid: 'Pagata',
    overdue: 'Scaduta',
    cancelled: 'Annullata',
  };
  const style = statusColors[invoice.status] || statusColors.draft;

  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        padding: '0.75rem',
        background: 'white',
        borderRadius: '0.5rem',
        border: '1px solid var(--line, #e5e7eb)',
        cursor: onClick ? 'pointer' : 'default',
      }}
    >
      <FileText size={20} style={{ color: style.color }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 500 }}>{invoice.number || invoice.id}</div>
        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted, #9ca3af)' }}>
          {formatDate(invoice.date)}
        </div>
      </div>
      <div style={{ textAlign: 'right' }}>
        <div style={{ fontWeight: 600 }}>{formatCurrency(invoice.total)}</div>
        <span
          style={{
            padding: '0.125rem 0.375rem',
            borderRadius: '9999px',
            fontSize: '0.625rem',
            fontWeight: 500,
            background: style.bg,
            color: style.color,
          }}
        >
          {statusLabels[invoice.status] || invoice.status}
        </span>
      </div>
    </div>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function CustomerDetailView({
  customer,
  contacts = [],
  projects = [],
  onBack,
  onEdit,
  onContactClick,
  onProjectClick,
  onInvoiceClick,
}: CustomerDetailViewProps) {
  const displayName = getCustomerDisplayName(customer);

  // Get related invoices
  const customerInvoices = useMemo(() => {
    return invoicesData.filter(
      (inv) =>
        inv.customerId === customer.id ||
        inv.customerName?.toLowerCase() === displayName.toLowerCase()
    );
  }, [customer.id, displayName]);

  // Get related contacts (matching company name)
  const relatedContacts = useMemo(() => {
    if (customer.customerType !== 'company') return [];
    const companyName = customer.companyName?.toLowerCase();
    return contacts.filter((c) => c.company?.toLowerCase() === companyName);
  }, [contacts, customer]);

  // Get related projects
  const relatedProjects = useMemo(() => {
    return projects.filter(
      (p) =>
        p.clientId === customer.id ||
        p.clientName?.toLowerCase() === displayName.toLowerCase() ||
        p.clientName?.toLowerCase() === customer.companyName?.toLowerCase()
    );
  }, [projects, customer, displayName]);

  // Calculate statistics
  const stats = useMemo(() => {
    const totalInvoiced = customerInvoices.reduce((sum, inv) => sum + inv.total, 0);
    const totalPaid = customerInvoices
      .filter((inv) => inv.status === 'paid')
      .reduce((sum, inv) => sum + inv.total, 0);
    const totalPending = customerInvoices
      .filter((inv) => ['sent', 'overdue'].includes(inv.status))
      .reduce((sum, inv) => sum + inv.total, 0);
    const overdueCount = customerInvoices.filter((inv) => inv.status === 'overdue').length;
    const activeProjects = relatedProjects.filter((p) => !['chiuso', 'in-pausa'].includes(p.status)).length;

    return {
      totalInvoiced,
      totalPaid,
      totalPending,
      overdueCount,
      invoiceCount: customerInvoices.length,
      projectCount: relatedProjects.length,
      activeProjects,
      contactCount: relatedContacts.length,
    };
  }, [customerInvoices, relatedProjects, relatedContacts]);

  return (
    <div style={{ padding: '1rem', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '1rem',
          marginBottom: '1.5rem',
          background: 'white',
          padding: '1.25rem',
          borderRadius: '0.75rem',
          border: '1px solid var(--line, #e5e7eb)',
        }}
      >
        <button
          onClick={onBack}
          style={{
            background: 'none',
            border: 'none',
            padding: '0.5rem',
            cursor: 'pointer',
            borderRadius: '0.375rem',
          }}
        >
          <ArrowLeft size={20} />
        </button>

        <div
          style={{
            width: '56px',
            height: '56px',
            borderRadius: '0.5rem',
            background: customer.customerType === 'company' ? '#dbeafe' : '#dcfce7',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {customer.customerType === 'company' ? (
            <Building2 size={28} style={{ color: '#3b82f6' }} />
          ) : (
            <User size={28} style={{ color: '#22c55e' }} />
          )}
        </div>

        <div style={{ flex: 1 }}>
          <h1 style={{ margin: 0, fontSize: '1.375rem', fontWeight: 600 }}>{displayName}</h1>
          <div
            style={{
              display: 'flex',
              gap: '1rem',
              marginTop: '0.5rem',
              fontSize: '0.875rem',
              color: 'var(--text-secondary, #6b7280)',
              flexWrap: 'wrap',
            }}
          >
            {customer.email && (
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                <Mail size={14} /> {customer.email}
              </span>
            )}
            {customer.phone && (
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                <Phone size={14} /> {customer.phone}
              </span>
            )}
            {customer.address.city && (
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                <MapPin size={14} /> {customer.address.city}, {customer.address.province}
              </span>
            )}
          </div>
          {customer.vatNumber && (
            <div style={{ marginTop: '0.5rem', fontSize: '0.8125rem', color: 'var(--text-muted, #9ca3af)' }}>
              P.IVA: {customer.vatNumber}
            </div>
          )}
        </div>

        <button
          onClick={onEdit}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.375rem',
            padding: '0.5rem 1rem',
            background: 'var(--brand, #3b82f6)',
            color: 'white',
            border: 'none',
            borderRadius: '0.375rem',
            cursor: 'pointer',
            fontWeight: 500,
            fontSize: '0.875rem',
          }}
        >
          <Edit size={16} /> Modifica
        </button>
      </div>

      {/* Stats Grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '1rem',
          marginBottom: '1.5rem',
        }}
      >
        <StatCard
          icon={<FileText size={18} />}
          label="Fatturato totale"
          value={formatCurrency(stats.totalInvoiced)}
          subvalue={`${stats.invoiceCount} fatture`}
          color="#3b82f6"
        />
        <StatCard
          icon={<TrendingUp size={18} />}
          label="Incassato"
          value={formatCurrency(stats.totalPaid)}
          color="#22c55e"
        />
        <StatCard
          icon={<Clock size={18} />}
          label="Da incassare"
          value={formatCurrency(stats.totalPending)}
          subvalue={stats.overdueCount > 0 ? `${stats.overdueCount} scadute` : undefined}
          color={stats.overdueCount > 0 ? '#ef4444' : '#f59e0b'}
        />
        <StatCard
          icon={<FolderKanban size={18} />}
          label="Progetti"
          value={stats.projectCount}
          subvalue={`${stats.activeProjects} attivi`}
          color="#8b5cf6"
        />
      </div>

      {/* Content Grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
          gap: '1.5rem',
        }}
      >
        {/* Contacts Section */}
        {customer.customerType === 'company' && (
          <div
            style={{
              background: 'var(--bg-secondary, #f9fafb)',
              borderRadius: '0.75rem',
              padding: '1rem',
            }}
          >
            <SectionHeader
              icon={<Users size={18} style={{ color: '#3b82f6' }} />}
              title="Contatti"
              count={relatedContacts.length}
            />
            {relatedContacts.length === 0 ? (
              <div
                style={{
                  textAlign: 'center',
                  padding: '2rem 1rem',
                  color: 'var(--text-muted, #9ca3af)',
                }}
              >
                <Users size={32} style={{ opacity: 0.5, marginBottom: '0.5rem' }} />
                <p style={{ margin: 0, fontSize: '0.875rem' }}>Nessun contatto associato</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {relatedContacts.slice(0, 5).map((contact) => (
                  <ContactCard
                    key={contact.id}
                    contact={contact}
                    onClick={onContactClick ? () => onContactClick(contact.id) : undefined}
                  />
                ))}
                {relatedContacts.length > 5 && (
                  <div
                    style={{
                      textAlign: 'center',
                      padding: '0.5rem',
                      color: 'var(--brand, #3b82f6)',
                      fontSize: '0.875rem',
                      cursor: 'pointer',
                    }}
                  >
                    +{relatedContacts.length - 5} altri contatti
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Projects Section */}
        <div
          style={{
            background: 'var(--bg-secondary, #f9fafb)',
            borderRadius: '0.75rem',
            padding: '1rem',
          }}
        >
          <SectionHeader
            icon={<FolderKanban size={18} style={{ color: '#8b5cf6' }} />}
            title="Progetti"
            count={relatedProjects.length}
          />
          {relatedProjects.length === 0 ? (
            <div
              style={{
                textAlign: 'center',
                padding: '2rem 1rem',
                color: 'var(--text-muted, #9ca3af)',
              }}
            >
              <FolderKanban size={32} style={{ opacity: 0.5, marginBottom: '0.5rem' }} />
              <p style={{ margin: 0, fontSize: '0.875rem' }}>Nessun progetto associato</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {relatedProjects.slice(0, 5).map((project) => (
                <ProjectCard
                  key={project.id}
                  project={project}
                  onClick={onProjectClick ? () => onProjectClick(project.id) : undefined}
                />
              ))}
              {relatedProjects.length > 5 && (
                <div
                  style={{
                    textAlign: 'center',
                    padding: '0.5rem',
                    color: 'var(--brand, #3b82f6)',
                    fontSize: '0.875rem',
                    cursor: 'pointer',
                  }}
                >
                  +{relatedProjects.length - 5} altri progetti
                </div>
              )}
            </div>
          )}
        </div>

        {/* Invoices Section */}
        <div
          style={{
            background: 'var(--bg-secondary, #f9fafb)',
            borderRadius: '0.75rem',
            padding: '1rem',
          }}
        >
          <SectionHeader
            icon={<FileText size={18} style={{ color: '#f59e0b' }} />}
            title="Fatture"
            count={customerInvoices.length}
          />
          {customerInvoices.length === 0 ? (
            <div
              style={{
                textAlign: 'center',
                padding: '2rem 1rem',
                color: 'var(--text-muted, #9ca3af)',
              }}
            >
              <FileText size={32} style={{ opacity: 0.5, marginBottom: '0.5rem' }} />
              <p style={{ margin: 0, fontSize: '0.875rem' }}>Nessuna fattura emessa</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {customerInvoices.slice(0, 5).map((invoice) => (
                <InvoiceCard
                  key={invoice.id}
                  invoice={invoice}
                  onClick={onInvoiceClick ? () => onInvoiceClick(invoice.id) : undefined}
                />
              ))}
              {customerInvoices.length > 5 && (
                <div
                  style={{
                    textAlign: 'center',
                    padding: '0.5rem',
                    color: 'var(--brand, #3b82f6)',
                    fontSize: '0.875rem',
                    cursor: 'pointer',
                  }}
                >
                  +{customerInvoices.length - 5} altre fatture
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Notes Section */}
      {customer.notes && (
        <div
          style={{
            marginTop: '1.5rem',
            background: 'white',
            borderRadius: '0.75rem',
            padding: '1rem',
            border: '1px solid var(--line, #e5e7eb)',
          }}
        >
          <SectionHeader icon={<FileText size={18} style={{ color: '#6b7280' }} />} title="Note" />
          <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--text-secondary, #6b7280)', whiteSpace: 'pre-wrap' }}>
            {customer.notes}
          </p>
        </div>
      )}
    </div>
  );
}

export type { CustomerDetailViewProps };
